<template>
  <div class="tab-container">
    <app-cells>
      <app-input
        v-model="search_filter"
        class="input--300"
        name="search"
        placeholder="Search"
        type="search"
        @input.native="onLoanSearch"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <v-client-table
        ref="loanTable"
        :class="{ 'table-default--loading': table_preloader }"
        :columns="table.columns"
        :data="table.items"
        :options="table.options"
        class="table-default"
      >
        <template #print="{ row }">
          <div class="table-default__controls">
            <button
              v-if="!print_preload || print_preload !== `print_${row.id}`"
              type="button"
              :disabled="print_preload"
              @click="onPrint(row)"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </button>
            <img v-show="print_preload === `print_${row.id}`" src="@/assets/img/preloader-export.svg" alt="">
          </div>
        </template>
        <template #h__employee>
          <div class="table-default__left">Employee</div>
        </template>
        <template #h__position>
          <div class="table-default__left">Position</div>
        </template>
        <template #details="{ row }">
          <div class="table-default__controls staff-loans__details-control">
            <span
              :style="{ backgroundColor: '#77b017', cursor: row.actual_installments_count > 0 ? 'pointer' : 'default' }"
              class="status-tag status-tag--flex"
              @click.stop="toggleDetails(row)"
            >
              <span>{{ row.actual_installments_count }}</span>
              <img
                v-if="row.actual_installments_count > 0"
                :class="{ 'staff-loans__details-arrow--open': isDetailsOpen(row.id) }"
                alt=""
                src="@/assets/img/arrow-toggler-bottom-white.svg"
              >
              <img
                v-if="scheduleLoadingId === row.id"
                alt=""
                class="staff-loans__details-preloader"
                src="@/assets/img/preloader.svg"
              >
            </span>
          </div>
        </template>
        <template #status="{ row }">
          <span :style="{ backgroundColor: row.status.color }" class="status-tag">
            {{ row.status.name }}
          </span>
        </template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #position="{ row }">
          <div class="table-default__left">{{ row.employee.position }}</div>
        </template>
        <template #bank_account="{ row }">
          <div class="table-default__left table-default__bank-account-cell">
            <span>{{ bankAccountLabel(row.employee) }}</span>
            <button v-if="row.employee.bank_account" type="button" class="table-default__bank-account-copy-button" title="Copy bank account" aria-label="Copy bank account" @click="copyBankAccount(row.employee.bank_account)">
              <img src="@/assets/img/copy-account.svg" alt="">
            </button>
          </div>
        </template>
        <template #amount="{ row }">{{ row.amount.toLocaleString() }} TSh</template>
        <template #paid_amount="{ row }">{{ row.paid_amount.toLocaleString() }} TSh</template>
        <template #remaining_balance="{ row }">{{ row.remaining_balance.toLocaleString() }} TSh</template>
        <template #closed_at="{ row }">{{ formatDateTime(row.closed_at) }}</template>
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #child_row="{ row }">
          <v-client-table
            :columns="scheduleTable.columns"
            :data="row.schedule"
            :options="scheduleTable.options"
            class="table-default"
          >
            <template #h__payroll>
              <div class="table-default__left">Payroll</div>
            </template>
            <template #related_month="{ row: schedule }">{{ formatMonth(schedule.related_month) }}</template>
            <template #payroll="{ row: schedule }">
              <div class="table-default__left">
                <router-link
                  v-if="schedule.payroll"
                  :to="{ name: 'employees-payroll-employees', params: { id: schedule.payroll } }"
                  class="table-default__link-border"
                >
                  {{ formatMonth(schedule.payroll_month) }}
                </router-link>
                <span v-else>-</span>
              </div>
            </template>
            <template #status="{ row: schedule }">
              <span :style="{ backgroundColor: schedule.status.color }" class="status-tag">
                {{ schedule.status.name }}
              </span>
            </template>
            <template #source="{ row: schedule }">
              <span v-if="schedule.source" :style="{ backgroundColor: schedule.source.color }" class="status-tag">
                {{ schedule.source.name }}
              </span>
              <span v-else>-</span>
            </template>
            <template #audit="{ row: schedule }">
              <schedule-audit-tags :schedule="schedule"/>
            </template>
            <template #amount="{ row: schedule }">{{ schedule.amount.toLocaleString() }} TSh</template>
            <template #principal_amount="{ row: schedule }">{{ schedule.principal_amount.toLocaleString() }} TSh</template>
            <template #interest_amount="{ row: schedule }">{{ schedule.interest_amount.toLocaleString() }} TSh</template>
            <template #balance_before="{ row: schedule }">{{ schedule.balance_before.toLocaleString() }} TSh</template>
            <template #remaining_balance_after="{ row: schedule }">
              {{ schedule.remaining_balance_after.toLocaleString() }} TSh ({{ schedule.remaining_percent_after }}%)
            </template>
            <template #deducted_at="{ row: schedule }">{{ formatDateTime(schedule.deducted_at) }}</template>
          </v-client-table>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :clickHandler="onClickPagination"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getEmployeesStaffLoans, getEmployeesStaffLoansExportInstallments, getEmployeesStaffLoansShedule } from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'
import ScheduleAuditTags from './ScheduleAuditTags.vue'

export default {
  name: 'StaffLoansArchive',
  components: {ScheduleAuditTags},
  data() {
    return {
      search_filter: '',
      table: {
        items: [],
        columns: [
          'print',
          'details',
          'status',
          'employee',
          'position',
          'bank_account',
          'amount',
          'paid_amount',
          'remaining_balance',
          'installments',
          'closed_at',
          'created_at',
        ],
        options: {
          headings: {
            print: '',
            details: 'Details',
            status: 'Status',
            employee: 'Employee',
            position: 'Position',
            bank_account: 'Bank account',
            amount: 'Amount',
            paid_amount: 'Paid',
            remaining_balance: 'Remaining balance',
            installments: 'Installments',
            closed_at: 'Closed at',
            created_at: 'Created at',
          },
          showChildRowToggler: false,
          pagination: {show: false},
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      scheduleTable: {
        columns: [
          'related_month',
          'payroll',
          'status',
          'source',
          'audit',
          'principal_amount',
          'interest_amount',
          'amount',
          'balance_before',
          'remaining_balance_after',
          'deducted_at',
        ],
        options: {
          headings: {
            related_month: 'Month',
            payroll: 'Payroll',
            status: 'Status',
            source: 'Source',
            audit: 'Audit',
            principal_amount: 'Principal',
            interest_amount: 'Interest',
            amount: 'Total',
            balance_before: 'Balance before',
            remaining_balance_after: 'Balance after',
            deducted_at: 'Deducted at',
          },
          filterable: false,
          pagination: {show: false},
          sortable: [],
          perPage: 100,
          rowClassCallback: () => 'table-default__row',
        },
      },
      scheduleLoadingId: null,
      openedScheduleIds: [],
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      page_loaded: false,
      print_preload: null,
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems(openedDetails = []) {
      this.table_preloader = true
      return getEmployeesStaffLoans({
        page: this.pagination.page,
        page_size: 50,
        search: this.search_filter,
        category: 'archive'
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.openedScheduleIds = []
          this.table_preloader = false
          this.page_loaded = true

          const openedRows = this.table.items.filter(row => openedDetails.includes(row.id) && row.actual_installments_count > 0)
          if (!openedRows.length) return

          return new Promise(resolve => {
            this.$nextTick(() => {
              this.restoreDetails(openedRows).then(resolve)
            })
          })
        })
        .catch(() => {
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    refreshAfterAction() {
      return this.fetchItems(this.openedScheduleIds)
    },
    toggleDetails(row) {
      if (row.actual_installments_count < 1 || this.scheduleLoadingId !== null) return

      if (this.isDetailsOpen(row.id)) {
        this.$refs.loanTable.toggleChildRow(row.id)
        this.openedScheduleIds = this.openedScheduleIds.filter(id => id !== row.id)
        return
      }

      return this.loadDetails(row)
    },
    loadDetails(row) {
      this.scheduleLoadingId = row.id
      return getEmployeesStaffLoansShedule(row.id)
        .then(response => {
          const schedule = Array.isArray(response.data) ? response.data : response.data.results || [response.data]
          this.$set(row, 'schedule', schedule)
          this.$refs.loanTable.toggleChildRow(row.id)
          this.openedScheduleIds.push(row.id)
          this.scheduleLoadingId = null
        })
        .catch(() => {
          this.scheduleLoadingId = null
        })
    },
    restoreDetails(rows) {
      if (!rows.length) return Promise.resolve()

      const [row, ...rest] = rows
      return this.loadDetails(row).then(() => this.restoreDetails(rest))
    },
    isDetailsOpen(id) {
      return this.openedScheduleIds.includes(id)
    },
    onPrint(row) {
      const pdfWindow = window.open('', '_blank')
      this.print_preload = `print_${ row.id }`
      getEmployeesStaffLoansExportInstallments(row.id)
        .then(response => {
          const url = response.data && (response.data.url || response.data)
          if (!url) throw new Error('PDF URL is missing')
          if (pdfWindow) pdfWindow.location.replace(url)
          else window.location.href = url
        })
        .catch(() => {
          if (pdfWindow) pdfWindow.close()
          this.$notify({type: 'error', title: 'Unable to open PDF'})
        })
        .finally(() => { this.print_preload = null })
    },
    bankAccountLabel(employee) {
      if (!employee.bank_account) return '-'
      const bank = employee.bank && (employee.bank.short_name || employee.bank.name)
      return bank ? `${bank}: ${employee.bank_account}` : employee.bank_account
    },
    copyBankAccount(account) {
      return navigator.clipboard.writeText(account).then(() => {
        this.$notify({type: 'success', title: 'Copied to clipboard'})
      })
    },
    formatDateTime(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    formatMonth(date) {
      if (!date) return '-'
      return format(parseISO(date), 'MMM yyyy')
    },
    onLoanSearch() {
      this.searchLoan(this)
    },
    searchLoan: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass">
.staff-loans__details-preloader
  width: 20px
  height: 20px

.staff-loans__details-control
  width: 100px
  justify-content: center

.staff-loans__details-arrow--open
  transform: rotate(180deg)
</style>
